import React from "react";
import PropTypes from "prop-types";
import { onActivateKeyDown } from "../../../utils/keyboardActivate";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CircularProgress } from "@mui/material";
import {
  SubscriptionCard,
  PaymentCardView,
  WarningBox,
  DiscountCodeComponent,
  UpcomingRefundComponent,
} from "../SharedComponents";
import {
  StyledDialog,
  DialogHeader,
  HeaderTitle,
  Title,
  CloseButton,
  Separator,
  ScrollableContent,
  CardsContainer,
  ChevronContainer,
  MiddleContentContainer,
  WarningContainer,
  ButtonsContainer,
  CancelText,
} from "../SubscriptionModal.styled";
import Button from "../../Button";

/**
 * Modal component for upgrading subscriptions
 */
const UpgradeModal = ({
  open,
  onClose,
  onConfirm,
  fromPlan = {
    type: "premium",
    name: "Premium",
    price: "15",
    validityDate: "April 18, 2025",
  },
  toPlan = {
    type: "pro",
    name: "Pro Suite",
    price: "50",
    validityDate: "April 19, 2025",
  },
  discount = null,
  showRefundInfo = true,
  showPaymentCard = false,
  isPendingPayment = false,
  closeOnBackdropClick = false,
  upgradeButtonText = "Subscribe",
  loading = false,
  handlePaymentMethodChange = () => {},
  isActionDisabled = false,
  // Promo code props
  promoCode = "",
  discountInfo = null,
  codeError = "",
  validatingCode = false,
  onApplyPromoCode = () => {},
}) => {
  // Check localStorage for a promo code if none is provided
  const [effectivePromoCode, setEffectivePromoCode] = React.useState(promoCode);
  console.log("effectivePromoCode2", effectivePromoCode);
  React.useEffect(() => {
    // If promoCode prop is provided, use it
    if (promoCode) {
      setEffectivePromoCode(promoCode);
    }
    // Otherwise check localStorage
    else {
      const storedPromoCode = localStorage.getItem("promoCode");
      if (storedPromoCode) {
        setEffectivePromoCode(storedPromoCode);
      }
    }
  }, [promoCode, onApplyPromoCode]);

  // Check if the plan is annual subscription
  const isAnnualSubscription = toPlan.durationDisplay === "Annually";

  // Calculate the correct monthly price if discount applied to annual plan
  const getDisplayPrice = () => {
    if (!discountInfo) return toPlan.price;

    // If it's an annual plan, divide by 12 to get the monthly price
    if (isAnnualSubscription) {
      return (parseFloat(discountInfo.discountedPrice) / 12).toFixed(2);
    }

    return discountInfo.discountedPrice;
  };

  // Same for original price if needed
  const getDisplayOriginalPrice = () => {
    if (!discountInfo) return null;

    if (isAnnualSubscription) {
      return (parseFloat(discountInfo.originalPrice) / 12).toFixed(2);
    }

    return discountInfo.originalPrice;
  };

  const handleBackdropClick = (event) => {
    // Only close if closeOnBackdropClick is true
    if (closeOnBackdropClick) {
      onClose(event);
    }
  };

  // Determine if the plans are annual or monthly
  const isFromPlanAnnual = fromPlan.isAnnual || false;
  const isToPlanAnnual = toPlan.isAnnual || false;

  const renderMiddleContent = () => {
    const components = [];

    // Add discount code component with effectivePromoCode
    components.push(
      <DiscountCodeComponent
        key="discount"
        onApplyCode={onApplyPromoCode}
        initialCode={effectivePromoCode}
        placeholder="Add Coupon here"
        showTitle={true}
        error={codeError}
        loading={validatingCode}
        discountInfo={discountInfo}
      />
    );

    // Add refund component
    if (showRefundInfo) {
      components.push(<UpcomingRefundComponent key="refund" />);
    }

    // Add payment component
    if (showPaymentCard) {
      components.push(
        <PaymentCardView
          onPaymentMethodChanged={handlePaymentMethodChange}
          key="payment"
        />
      );
    }

    return components.length > 0 ? components : null;
  };

  // For onConfirm, we should pass the promo code if it's valid
  const handleConfirm = () => {
    onConfirm({
      promoCode: discountInfo ? discountInfo.code : null,
    });
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      // a11y-i18n-mobileweb:I3/E-6 — see DowngradeModal's identical comment:
      // Esc-to-close must not be coupled to backdrop-click-to-close, or a
      // keyboard user is trapped on this money-adjacent modal by default.
      disableEscapeKeyDown={false}
      onClick={(e) => e.stopPropagation()}
      onBackdropClick={handleBackdropClick}
    >
      <DialogHeader>
        <HeaderTitle>
          <WarningAmberIcon style={{ color: "#F79A00" }} />
          <Title>Confirm Your Upgrade</Title>
        </HeaderTitle>
        <CloseButton
          role="button"
          tabIndex={0}
          aria-label="Close"
          onClick={onClose}
          onKeyDown={onActivateKeyDown(onClose)}
        >
          <CloseIcon />
        </CloseButton>
      </DialogHeader>

      <Separator />

      <ScrollableContent>
        <CardsContainer>
          <SubscriptionCard
            planType={fromPlan.type}
            planName={fromPlan.name}
            price={fromPlan.price}
            interval="/month"
            validityLabel="Until"
            validityDate={fromPlan.validityDate}
            isActive={true}
            durationDisplay={fromPlan.durationDisplay}
            yearlyPrice={fromPlan.yearlyPrice}
          />

          <ChevronContainer>
            <ChevronRightIcon style={{ color: "#9B5AF8", fontSize: 40 }} />
          </ChevronContainer>

          <SubscriptionCard
            planType={toPlan.type}
            planName={toPlan.name}
            price={getDisplayPrice()}
            originalPrice={getDisplayOriginalPrice()}
            interval="/month"
            validityLabel="Starting from"
            validityDate={toPlan.validityDate}
            durationDisplay={toPlan.durationDisplay}
            yearlyPrice={
              discountInfo ? discountInfo.discountedPrice : toPlan.yearlyPrice
            }
          />
        </CardsContainer>

        {renderMiddleContent() && (
          <MiddleContentContainer>
            {renderMiddleContent()}
          </MiddleContentContainer>
        )}

        <WarningContainer>
          <WarningBox isUpgrade={true} hasRefund={showRefundInfo} />
        </WarningContainer>
      </ScrollableContent>

      <ButtonsContainer>
        <CancelText
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={onActivateKeyDown(onClose)}
        >
          Cancel
        </CancelText>
        <Button
          width="auto"
          style={{minWidth:120}}
          onClick={handleConfirm}
          disabled={loading || isActionDisabled}
        >
          {loading ? (
            <CircularProgress size={20} style={{ color: "white" }} />
          ) : (
            upgradeButtonText
          )}
        </Button>
      </ButtonsContainer>
    </StyledDialog>
  );
};

UpgradeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  fromPlan: PropTypes.shape({
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    validityDate: PropTypes.string.isRequired,
    price_id: PropTypes.string.isRequired,
    product_id: PropTypes.string.isRequired,
    isAnnual: PropTypes.bool,
    monthlyPrice: PropTypes.string,
    yearlyPrice: PropTypes.string,
  }),
  toPlan: PropTypes.shape({
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    validityDate: PropTypes.string.isRequired,
    price_id: PropTypes.string.isRequired,
    product_id: PropTypes.string.isRequired,
    isAnnual: PropTypes.bool,
    monthlyPrice: PropTypes.string,
    yearlyPrice: PropTypes.string,
  }),
  discount: PropTypes.shape({
    code: PropTypes.string.isRequired,
    discountedPrice: PropTypes.string.isRequired,
  }),
  showRefundInfo: PropTypes.bool,
  showPaymentCard: PropTypes.bool,
  isPendingPayment: PropTypes.bool,
  closeOnBackdropClick: PropTypes.bool,
  upgradeButtonText: PropTypes.string,
  loading: PropTypes.bool,
  isActionDisabled: PropTypes.bool,
  // Promo code props
  promoCode: PropTypes.string,
  discountInfo: PropTypes.object,
  codeError: PropTypes.string,
  validatingCode: PropTypes.bool,
  onApplyPromoCode: PropTypes.func,
};

export default UpgradeModal;
